import {MinMaxArea, PremisesObject, PremisesType} from 'components/pages/PremisesStartPage/types';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {ResidenceCardData} from 'features/search/types';
import {ConceptType, ObjectStatus, PremiseStatus, StageStatus} from 'features/search/filters/enums';
import {getCurrentSiteId} from 'features/services/utils/env-helper';
import {AREA_FILTER_MAX, AREA_FILTER_MIN} from 'features/premises/constants';

export const isPremiseSoldOrRented = (status?: PremiseStatus | string): boolean =>
    status === PremiseStatus.SOLD || status === PremiseStatus.RENTED;

/**
 * Sold and rented premises are excluded from the search/grid view but stay
 * reachable through the premises page ("Andra lokaler").
 */
export const filterAvailablePremises = (premises: PremisesObject[]): PremisesObject[] =>
    premises.filter(premise => !isPremiseSoldOrRented(premise.status));

/**
 * Area filter: a premise's area is its `size` (m²) – minArea/maxArea/primaryArea are
 * residence-concepts and are 0 for premises. Matches when size falls within the selected
 * range. The default full range matches everything, and a max at the slider ceiling also
 * keeps anything larger than the ceiling.
 */
export const premiseMatchesArea = (premise: PremisesObject, minMax: MinMaxArea): boolean => {
    if (minMax.min === AREA_FILTER_MIN && minMax.max === AREA_FILTER_MAX) return true;

    if (premise.size >= minMax.min && premise.size <= minMax.max) return true;
    if (minMax.max === AREA_FILTER_MAX && premise.size > AREA_FILTER_MAX) return true;

    return false;
};

/**
 * Type filter: premise type ids are carried in `type` (not `tags` – those are other ids).
 * No selected types matches everything.
 */
export const premiseMatchesTypes = (premise: PremisesObject, types: PremisesType[]): boolean =>
    types.length === 0 || types.some(type => premise.type.includes(type));

/**
 * Available premises (not sold/rented) narrowed by the active area + type filters.
 */
export const filterPremises = (
    premises: PremisesObject[],
    minMax: MinMaxArea,
    types: PremisesType[],
): PremisesObject[] =>
    filterAvailablePremises(premises)
        .filter(premise => premiseMatchesArea(premise, minMax))
        .filter(premise => premiseMatchesTypes(premise, types));

/**
 * Maps a PremisesObject into the minimal shape ResidenceCard consumes.
 * Premises are always shown on the current site, so siteId is derived from
 * the active site rather than the object's siteOrg. Premises carry their own
 * PremiseStatus; until premises-specific card content is ported, the
 * status/showing chrome uses neutral "for sale" defaults so showings surface.
 */
export const premiseToResidenceCardData = (premise: PremisesObject): ResidenceCardData => ({
    apartmentNumber: premise.objectNumber,
    area: premise.area,
    concept: ConceptType.JM_ORIGINAL,
    displayName: premise.address || premise.name,
    id: premise.id,
    inhabitDate: premise.inhabitDate,
    inhabitDateDetermined: premise.inhabitDateDetermined,
    inhabitDateEnd: premise.inhabitDateEnd,
    maxRooms: undefined,
    minRooms: undefined,
    municipality: premise.municipality,
    name: premise.name,
    objectStatus: ObjectStatus.FOR_SALE,
    searchImage: premise.image,
    showings: premise.showings,
    siteId: getCurrentSiteId(),
    size: premise.size,
    stage: premise.stage,
    stageStatus: StageStatus.FORSALE,
    status: premise.status,
    type: 'premises',
    types: premise.type,
    url: premise.url,
});

export const translatePremiseEnum = (type: PremisesType) => {
    switch (type) {
        case PremisesType.STORE:
            return getFormattedMessage("jm.components.PremisesType.store");
        case PremisesType.OFFICE:
            return getFormattedMessage("jm.components.PremisesType.office");
        case PremisesType.RESTAURANT_CAFE:
            return getFormattedMessage("jm.components.PremisesType.restaurantCafe");
        case PremisesType.GYM:
            return getFormattedMessage("jm.components.PremisesType.gym");
        case PremisesType.INDUSTRIAL_WORKSHOP:
            return getFormattedMessage("jm.components.PremisesType.industrialWorkshop");
        case PremisesType.OFFICE_HOTEL:
            return getFormattedMessage("jm.components.PremisesType.officeHotel");
        case PremisesType.WAREHOUSE:
            return getFormattedMessage("jm.components.PremisesType.warehouse");
        case PremisesType.OTHER:
            return getFormattedMessage("jm.components.PremisesType.other");
        default:
            return '';
    }
};
